import React, { useState, useEffect } from 'react';
import AvailableDeliveries from './AvailableDeliveries';
import ActiveDeliveries from './ActiveDeliveries';
import PackageScanner from './PackageScanner';
import DeliveryRoute from './DeliveryRoute';

function DriverDashboard({ driver, onLogout }) {
  const [view, setView] = useState('available'); // 'available', 'active', 'scanner', 'route', 'earnings'
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [stats, setStats] = useState(driver.stats);

  // Load active deliveries from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('driver_active_deliveries');
    if (saved) {
      try {
        setActiveDeliveries(JSON.parse(saved));
      } catch (err) {
        console.error('Failed to load active deliveries:', err);
      }
    }
  }, []);

  // Save active deliveries to localStorage
  useEffect(() => {
    if (activeDeliveries.length > 0) {
      localStorage.setItem('driver_active_deliveries', JSON.stringify(activeDeliveries));
    } else {
      localStorage.removeItem('driver_active_deliveries');
    }
  }, [activeDeliveries]);

  const handlePickupDeliveries = (deliveries) => {
    setSelectedDeliveries(deliveries);
    setView('scanner');
  };

  const handleScanComplete = (scannedPackages) => {
    // Add scanned packages to active deliveries
    const newActive = scannedPackages.map(pkg => ({
      ...pkg,
      status: 'in_transit',
      pickedUpAt: new Date().toISOString()
    }));
    
    setActiveDeliveries([...activeDeliveries, ...newActive]);
    setSelectedDeliveries([]);
    
    // Generate route
    const route = {
      deliveries: newActive,
      totalDistance: newActive.reduce((sum, d) => sum + d.distance, 0),
      estimatedTime: Math.ceil(newActive.reduce((sum, d) => sum + d.distance, 0) / 25 * 60), // km/h to minutes
      stops: newActive.map((d, idx) => ({
        order: idx + 1,
        address: d.deliveryAddress,
        customer: d.customerName,
        phone: d.customerPhone,
        deliveryCode: d.deliveryCode
      }))
    };
    
    setCurrentRoute(route);
    setView('route');
  };

  const handleDeliveryComplete = (deliveryId, deliveryCode) => {
    // Verify delivery code and mark as complete
    const delivery = activeDeliveries.find(d => d.id === deliveryId);
    if (!delivery) return false;

    if (delivery.deliveryCode !== deliveryCode) {
      return false; // Wrong code
    }

    // Mark as delivered
    const updatedDeliveries = activeDeliveries.filter(d => d.id !== deliveryId);
    setActiveDeliveries(updatedDeliveries);

    // Update earnings
    const newStats = {
      ...stats,
      totalDeliveries: stats.totalDeliveries + 1,
      todayDeliveries: stats.todayDeliveries + 1,
      totalEarnings: stats.totalEarnings + delivery.payment
    };
    setStats(newStats);

    // Update driver data in localStorage
    const updatedDriver = {
      ...driver,
      stats: newStats
    };
    localStorage.setItem('homie_driver', JSON.stringify(updatedDriver));

    // If no more deliveries, go back to available view
    if (updatedDeliveries.length === 0) {
      setCurrentRoute(null);
      setView('available');
    } else {
      // Update route
      const updatedRoute = {
        ...currentRoute,
        deliveries: updatedDeliveries,
        stops: currentRoute.stops.filter(s => s.deliveryCode !== deliveryCode)
      };
      setCurrentRoute(updatedRoute);
    }

    return true;
  };

  const handleCancelScanner = () => {
    setSelectedDeliveries([]);
    setView('available');
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🚗 Homie Driver</h1>
            <span className={`status-badge status-${driver.status}`}>
              {driver.status === 'available' ? '🟢 Available' : 
               driver.status === 'busy' ? '🟡 Busy' : '⚫ Offline'}
            </span>
          </div>
          <div className="header-right">
            <div className="driver-info">
              <span className="driver-name">{driver.name}</span>
              <span className="driver-phone">{driver.phone}</span>
            </div>
            <button className="btn-logout" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <div className="stat-value">{stats.todayDeliveries}</div>
            <div className="stat-label">Today's Deliveries</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">€{stats.totalEarnings.toFixed(2)}</div>
            <div className="stat-label">Total Earnings</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <div className="stat-value">{stats.rating.toFixed(1)}</div>
            <div className="stat-label">Rating</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDeliveries}</div>
            <div className="stat-label">Total Deliveries</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="dashboard-nav">
        <button 
          className={`nav-item ${view === 'available' ? 'active' : ''}`}
          onClick={() => setView('available')}
        >
          <span className="nav-icon">📋</span>
          Available
          {view === 'available' && <span className="nav-indicator"></span>}
        </button>
        <button 
          className={`nav-item ${view === 'active' || view === 'route' ? 'active' : ''}`}
          onClick={() => setView(activeDeliveries.length > 0 ? 'route' : 'active')}
        >
          <span className="nav-icon">🚚</span>
          Active ({activeDeliveries.length})
          {(view === 'active' || view === 'route') && <span className="nav-indicator"></span>}
        </button>
        <button 
          className={`nav-item ${view === 'earnings' ? 'active' : ''}`}
          onClick={() => setView('earnings')}
        >
          <span className="nav-icon">💵</span>
          Earnings
          {view === 'earnings' && <span className="nav-indicator"></span>}
        </button>
      </nav>

      {/* Main Content */}
      <main className="dashboard-content">
        {view === 'available' && (
          <AvailableDeliveries 
            driverLocation={driver.currentLocation}
            onPickup={handlePickupDeliveries}
            maxPackages={activeDeliveries.length > 0 ? 5 - activeDeliveries.length : 5}
          />
        )}
        
        {view === 'active' && (
          <ActiveDeliveries 
            deliveries={activeDeliveries}
            onViewRoute={() => currentRoute && setView('route')}
          />
        )}

        {view === 'scanner' && (
          <PackageScanner 
            selectedDeliveries={selectedDeliveries}
            onComplete={handleScanComplete}
            onCancel={handleCancelScanner}
          />
        )}

        {view === 'route' && currentRoute && (
          <DeliveryRoute 
            route={currentRoute}
            onDeliveryComplete={handleDeliveryComplete}
            onBackToActive={() => setView('active')}
          />
        )}

        {view === 'earnings' && (
          <div className="earnings-view">
            <h2>Earnings Summary</h2>
            <div className="earnings-cards">
              <div className="earnings-card">
                <h3>Today</h3>
                <div className="earnings-amount">€{(stats.todayDeliveries * 2.5).toFixed(2)}</div>
                <p>{stats.todayDeliveries} deliveries</p>
              </div>
              <div className="earnings-card">
                <h3>This Week</h3>
                <div className="earnings-amount">€{stats.totalEarnings.toFixed(2)}</div>
                <p>{stats.totalDeliveries} deliveries</p>
              </div>
              <div className="earnings-card">
                <h3>Average per Delivery</h3>
                <div className="earnings-amount">
                  €{stats.totalDeliveries > 0 ? (stats.totalEarnings / stats.totalDeliveries).toFixed(2) : '0.00'}
                </div>
                <p>Based on distance</p>
              </div>
            </div>
            
            <div className="pricing-info">
              <h3>Pricing Rates</h3>
              <div className="pricing-table">
                <div className="pricing-row">
                  <span className="distance">📍 Less than 2km</span>
                  <span className="price">€2.50</span>
                </div>
                <div className="pricing-row">
                  <span className="distance">📍 2km - 4km</span>
                  <span className="price">€2.80</span>
                </div>
                <div className="pricing-row">
                  <span className="distance">📍 More than 4km</span>
                  <span className="price">€3.00</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default DriverDashboard;
